import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Avatar,
  useToast,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiEye,
  FiEdit,
  FiPlus,
  FiSearch,
  FiDownload,
  FiTrash2,
  FiMoreVertical,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiClock,
} from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";
import DocumentModal from "./DocumentModal";

const DocumentsTab = ({
  employees,
  onEmployeeSelect,
  onViewProfile,
  refreshData,
}) => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [documents, setDocuments] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Fetch documents for all employees
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await digitalFilesService.documents.getAllDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Error loading documents",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleUploadDocument = (employee) => {
    setSelectedEmployee(employee);
    onOpen();
  };

  const handleDownloadDocument = async (document) => {
    try {
      const response = await digitalFilesService.documents.downloadDocument(
        document._id
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", document.originalFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: "Download started",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await digitalFilesService.documents.deleteDocument(documentId);
        toast({
          title: "Document deleted",
          status: "success",
          duration: 2000,
        });
        fetchDocuments();
        refreshData();
      } catch (error) {
        toast({
          title: "Delete failed",
          description: error.message,
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const handleVerifyDocument = async (document, status) => {
    try {
      await digitalFilesService.documents.verifyDocument(document._id, {
        status,
        notes: `Document ${status.toLowerCase()} by admin`,
      });
      toast({
        title: `Document ${status.toLowerCase()}`,
        status: "success",
        duration: 2000,
      });
      fetchDocuments();
    } catch (error) {
      toast({
        title: "Verification failed",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  // Filter documents based on search and filters
  const filteredDocuments = documents.filter((doc) => {
    const employee = employees.find((emp) => emp.employeeId === doc.employeeId);
    const employeeName = employee?.name || "";

    const matchesSearch = [
      doc.documentName,
      doc.documentType,
      employeeName,
      doc.employeeId,
    ]
      .join(" ")
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesType =
      !documentTypeFilter || doc.documentType === documentTypeFilter;
    const matchesStatus =
      !statusFilter || doc.verificationStatus === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Verified":
        return "green";
      case "Rejected":
        return "red";
      case "Pending":
        return "orange";
      default:
        return "gray";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Verified":
        return <FiCheckCircle />;
      case "Rejected":
        return <FiXCircle />;
      case "Pending":
        return <FiClock />;
      default:
        return <FiFileText />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const documentTypes = [
    "Resume/CV",
    "Offer Letter",
    "Employment Contract",
    "ID Proof",
    "Address Proof",
    "Educational Certificates",
    "Experience Letters",
    "Salary Slips",
    "Bank Statements",
    "Tax Documents",
    "Medical Certificates",
    "Training Certificates",
    "Performance Reviews",
    "Appraisal Letters",
    "Promotion Letters",
    "Termination/Resignation Letters",
    "NDA/Confidentiality Agreement",
    "Background Check Report",
    "Reference Letters",
    "Other",
  ];

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="xl" fontWeight="bold" color="gray.800">
            Document Management
          </Text>
          <Text color={mutedColor}>
            Upload, verify, and manage employee documents securely
          </Text>
        </Box>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => {
            setSelectedEmployee(null);
            onOpen();
          }}
        >
          Upload Document
        </Button>
      </Flex>

      {/* Filters */}
      <Flex gap={4} mb={6} wrap="wrap">
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <FiSearch color="gray" />
          </InputLeftElement>
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>

        <Select
          placeholder="All Document Types"
          value={documentTypeFilter}
          onChange={(e) => setDocumentTypeFilter(e.target.value)}
          maxW="200px"
        >
          {documentTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>

        <Select
          placeholder="All Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          maxW="150px"
        >
          <option value="Pending">Pending</option>
          <option value="Verified">Verified</option>
          <option value="Rejected">Rejected</option>
          <option value="Expired">Expired</option>
        </Select>
      </Flex>

      {/* Documents Table */}
      <Box
        overflowX="auto"
        borderRadius="lg"
        border="1px"
        borderColor="gray.200"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Document</Th>
              <Th>Employee</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Size</Th>
              <Th>Uploaded</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredDocuments.map((document) => {
              const employee = employees.find(
                (emp) => emp.employeeId === document.employeeId
              );

              return (
                <Tr key={document._id} _hover={{ bg: "gray.50" }}>
                  <Td>
                    <HStack spacing={3}>
                      <FiFileText size={20} />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{document.documentName}</Text>
                        <Text fontSize="sm" color={mutedColor}>
                          {document.originalFileName}
                        </Text>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Avatar size="sm" name={employee?.name} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">{employee?.name || "Unknown"}</Text>
                        <Text fontSize="xs" color={mutedColor}>
                          {document.employeeId}
                        </Text>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td>
                    <Badge variant="outline">{document.documentType}</Badge>
                  </Td>
                  <Td>
                    <HStack>
                      {getStatusIcon(document.verificationStatus)}
                      <Badge
                        colorScheme={getStatusBadgeColor(
                          document.verificationStatus
                        )}
                      >
                        {document.verificationStatus}
                      </Badge>
                    </HStack>
                  </Td>
                  <Td>{formatFileSize(document.fileSize)}</Td>
                  <Td>
                    <Text fontSize="sm">
                      {new Date(document.createdAt).toLocaleDateString()}
                    </Text>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<FiDownload />}
                          onClick={() => handleDownloadDocument(document)}
                        >
                          Download
                        </MenuItem>
                        <MenuItem
                          icon={<FiEye />}
                          onClick={() => {
                            setSelectedEmployee(employee);
                            onViewProfile(employee);
                          }}
                        >
                          View Employee Profile
                        </MenuItem>
                        {document.verificationStatus === "Pending" && (
                          <>
                            <MenuItem
                              icon={<FiCheckCircle />}
                              onClick={() =>
                                handleVerifyDocument(document, "Verified")
                              }
                            >
                              Verify Document
                            </MenuItem>
                            <MenuItem
                              icon={<FiXCircle />}
                              onClick={() =>
                                handleVerifyDocument(document, "Rejected")
                              }
                            >
                              Reject Document
                            </MenuItem>
                          </>
                        )}
                        <MenuItem
                          icon={<FiTrash2 />}
                          color="red.500"
                          onClick={() => handleDeleteDocument(document._id)}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Document Stats */}
      {documents.length > 0 && (
        <Box mt={8} p={6} bg="gray.50" borderRadius="lg">
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Document Statistics
          </Text>
          <StatGroup>
            <Stat>
              <StatLabel>Total Documents</StatLabel>
              <StatNumber>{documents.length}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Verified Documents</StatLabel>
              <StatNumber>
                {
                  documents.filter(
                    (doc) => doc.verificationStatus === "Verified"
                  ).length
                }
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Pending Verification</StatLabel>
              <StatNumber>
                {
                  documents.filter(
                    (doc) => doc.verificationStatus === "Pending"
                  ).length
                }
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total Size</StatLabel>
              <StatNumber>
                {formatFileSize(
                  documents.reduce((sum, doc) => sum + doc.fileSize, 0)
                )}
              </StatNumber>
            </Stat>
          </StatGroup>
        </Box>
      )}

      {/* Document Modal */}
      <DocumentModal
        isOpen={isOpen}
        onClose={onClose}
        employee={selectedEmployee}
        onSuccess={() => {
          fetchDocuments();
          refreshData();
        }}
      />
    </Box>
  );
};

export default DocumentsTab;
